import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const FluidGeometryBackground = () => {
  const containerRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const smoothedMouseRef = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // 1. 场景初始化
    const scene = new THREE.Scene();
    // 增加雾效，让远处的粒子有点深邃感
    scene.fog = new THREE.FogExp2(0x000000, 0.02);

    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({
      antialias: false, // 关闭抗锯齿以提高性能（粒子多了看不出来）
      alpha: false,
      powerPreference: 'high-performance',
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // 限制像素比，防卡顿
    renderer.setClearColor(0x000000, 1);
    container.appendChild(renderer.domElement);

    // 2. 粒子设置
    const particleCount = 4000; // 稍微增加一点密度

    // 创建基础几何体
    const geometry = new THREE.PlaneGeometry(1, 1);
    const instancedGeometry = new THREE.InstancedBufferGeometry();
    instancedGeometry.copy(geometry);

    // 属性数组
    const shapeTypes = new Float32Array(particleCount);
    const sizes = new Float32Array(particleCount);
    const randoms = new Float32Array(particleCount);
    const initialPositions = new Float32Array(particleCount * 3);

    const random = () => Math.random();
    const randomRange = (min, max) => Math.random() * (max - min) + min;

    for (let i = 0; i < particleCount; i++) {
      // 扩大 Z 轴范围，增加景深感
      const x = randomRange(-18, 18);
      const y = randomRange(-18, 18);
      const z = randomRange(-10, 10);

      initialPositions[i * 3] = x;
      initialPositions[i * 3 + 1] = y;
      initialPositions[i * 3 + 2] = z;

      // 调整大小：缩小60%，细腻的"尘埃感"
      sizes[i] = randomRange(0.04, 0.32); // 原来是 0.1-0.8，缩小60%后约 0.04-0.32

      // 形状分配：圆形 40%，其他各 20%
      const rand = random();
      shapeTypes[i] = rand < 0.4 ? 0 : rand < 0.6 ? 1 : rand < 0.8 ? 2 : 3;

      randoms[i] = random();
    }

    instancedGeometry.setAttribute(
      'instanceShape',
      new THREE.InstancedBufferAttribute(shapeTypes, 1)
    );
    instancedGeometry.setAttribute(
      'instanceSize',
      new THREE.InstancedBufferAttribute(sizes, 1)
    );
    instancedGeometry.setAttribute(
      'instanceRandom',
      new THREE.InstancedBufferAttribute(randoms, 1)
    );
    instancedGeometry.setAttribute(
      'instanceInitialPosition',
      new THREE.InstancedBufferAttribute(initialPositions, 3)
    );

    const instancedMesh = new THREE.InstancedMesh(
      instancedGeometry,
      null,
      particleCount
    );

    // 物理状态存储
    const particleStates = [];
    for (let i = 0; i < particleCount; i++) {
      particleStates.push({
        initialPosition: new THREE.Vector3(
          initialPositions[i * 3],
          initialPositions[i * 3 + 1],
          initialPositions[i * 3 + 2]
        ),
        position: new THREE.Vector3(
          initialPositions[i * 3],
          initialPositions[i * 3 + 1],
          initialPositions[i * 3 + 2]
        ),
        velocity: new THREE.Vector3(0, 0, 0), // 添加速度向量，做惯性
        size: sizes[i],
        random: randoms[i],
      });
    }

    // 设置初始矩阵
    const tempObject = new THREE.Object3D();
    for (let i = 0; i < particleCount; i++) {
      const state = particleStates[i];
      tempObject.position.copy(state.position);
      tempObject.scale.set(state.size, state.size, 1);
      tempObject.updateMatrix();
      instancedMesh.setMatrixAt(i, tempObject.matrix);
    }
    instancedMesh.instanceMatrix.needsUpdate = true;

    // 3. Shader Material (核心修改：自发光与颜色)
    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uMouse: { value: new THREE.Vector2(0.5, 0.5) },
        uResolution: {
          value: new THREE.Vector2(window.innerWidth, window.innerHeight),
        },
      },
      vertexShader: `
        attribute float instanceShape;
        attribute float instanceSize;
        attribute float instanceRandom;
        
        varying vec2 vUv;
        varying float vShape;
        varying float vSize;
        varying float vRandom;
        varying vec3 vPosition;
        
        void main() {
          vUv = uv;
          vShape = instanceShape;
          vSize = instanceSize;
          vRandom = instanceRandom;
          
          vec4 worldPos = instanceMatrix * vec4(position, 1.0);
          vPosition = worldPos.xyz;
          
          gl_Position = projectionMatrix * viewMatrix * worldPos;
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform vec2 uMouse;
        uniform vec2 uResolution;
        
        varying vec2 vUv;
        varying float vShape;
        varying float vSize;
        varying float vRandom;
        varying vec3 vPosition;
        
        // 形状绘制函数（带羽化边缘）
        float drawCircle(vec2 uv, float radius) {
          float dist = length(uv - 0.5);
          return 1.0 - smoothstep(radius - 0.15, radius + 0.1, dist);
        }
        float drawSquare(vec2 uv, float size) {
          vec2 d = abs(uv - 0.5) - size * 0.5;
          float dist = length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
          return 1.0 - smoothstep(0.0, 0.15, dist);
        }
        float drawTriangle(vec2 uv) {
          vec2 p = (uv - 0.5) * 2.0;
          float r = length(p);
          float a = atan(p.y, p.x);
          float t = abs(cos(a * 3.0)) * 0.5;
          return 1.0 - smoothstep(t - 0.1, t + 0.1, r);
        }
        float drawPentagon(vec2 uv) {
          vec2 p = (uv - 0.5) * 2.0;
          float r = length(p);
          float a = atan(p.y, p.x);
          float pen = cos(a * 2.5) * 0.4;
          return 1.0 - smoothstep(pen - 0.1, pen + 0.1, r);
        }

        void main() {
          // 1. 形状计算
          float shape = 0.0;
          if (vShape < 0.5) shape = drawCircle(vUv, 0.4);
          else if (vShape < 1.5) shape = drawSquare(vUv, 0.75);
          else if (vShape < 2.5) shape = drawTriangle(vUv);
          else shape = drawPentagon(vUv);
          
          // 丢弃看不见的像素，优化性能
          if (shape < 0.01) discard;

          // 2. 计算鼠标距离（屏幕空间）
          vec2 mouseScreen = uMouse * uResolution;
          vec2 fragScreen = gl_FragCoord.xy;
          vec2 toMouseScreen = fragScreen - mouseScreen;
          float distToMouse = length(toMouseScreen);
          
          // 交互阈值（像素单位）- 增大范围确保能检测到
          float interactionRadius = 200.0; // 从120增加到200，确保更大的交互范围
          float interactionIntensity = 1.0 - smoothstep(0.0, interactionRadius, distToMouse);

          // 3. 基础颜色计算（蓝紫色）
          vec3 deepBlue = vec3(0.1, 0.2, 0.5);
          vec3 lightBlue = vec3(0.3, 0.5, 0.8);
          vec3 deepPurple = vec3(0.4, 0.1, 0.5);
          vec3 lightPurple = vec3(0.6, 0.3, 0.8);
          
          // 根据位置混合蓝紫色
          float colorMix = sin(vPosition.x * 0.1 + vPosition.y * 0.1 + vRandom * 10.0) * 0.5 + 0.5;
          vec3 color1 = mix(deepBlue, lightBlue, colorMix);
          vec3 color2 = mix(deepPurple, lightPurple, colorMix);
          vec3 baseColor = mix(color1, color2, sin(vRandom * 5.0) * 0.5 + 0.5);

          // 4. 星星闪烁效果（随机闪烁，不受鼠标影响）
          // 使用粒子的随机种子和时间创建闪烁
          float twinkleSpeed = 1.5; // 闪烁速度（稍微慢一点，更明显）
          float twinklePhase = vRandom * 6.28 + uTime * twinkleSpeed; // 每个粒子不同的相位
          float twinkle = sin(twinklePhase) * 0.5 + 0.5; // 0到1之间
          twinkle = pow(twinkle, 1.2); // 让闪烁更平滑明显（降低指数让变化更平滑）
          float twinkleIntensity = 0.3 + twinkle * 0.7; // 基础0.3，闪烁时到1.0（已经正确）

          // 5. 鼠标接触时的自发光（蓝紫色大幅增强）
          float mouseGlowIntensity = 0.0;
          if (distToMouse < interactionRadius) {
            // 鼠标接触时，大幅增强蓝紫色的自发光
            mouseGlowIntensity = interactionIntensity;
            
            // 让蓝紫色更亮更饱和，产生明显的自发光效果
            // 方法1：直接增强颜色亮度
            baseColor *= (1.0 + mouseGlowIntensity * 3.0); // 大幅增强亮度
            
            // 方法2：增加颜色的饱和度（让蓝紫色更鲜艳）
            vec3 gray = vec3(dot(baseColor, vec3(0.299, 0.587, 0.114)));
            baseColor = mix(gray, baseColor, 1.0 + mouseGlowIntensity * 1.5);
            
            // 方法3：添加额外的蓝紫色光晕
            baseColor += baseColor * mouseGlowIntensity * 2.0;
          }

          // 6. 最终颜色计算
          vec3 finalColor = baseColor * twinkleIntensity;
          
          // 鼠标接触时额外增强整体亮度
          if (distToMouse < interactionRadius) {
            finalColor *= (1.0 + mouseGlowIntensity * 2.0);
          }

          // 7. 亮度计算（光晕效果）
          float glow = pow(shape, 1.8);
          finalColor *= glow;
          
          // 基础亮度
          float baseBrightness = 0.5 + vSize * 0.2;
          finalColor *= baseBrightness;

          // 8. 透明度
          // 默认状态：根据闪烁调整透明度（提高基础透明度让粒子更可见）
          float defaultAlpha = 0.4 + twinkle * 0.4; // 提高基础透明度，闪烁时更亮
          // 鼠标接触时：透明度大幅增强（让自发光效果明显）
          float interactiveAlpha = 1.0; // 鼠标接触时完全不透明
          float alpha = mix(defaultAlpha, interactiveAlpha, interactionIntensity);
          alpha *= smoothstep(0.0, 1.0, shape); // 根据形状边缘衰减
          
          // 确保鼠标接触时alpha足够高，让自发光效果明显
          if (distToMouse < interactionRadius) {
            alpha = max(alpha, interactionIntensity * 0.9); // 至少90%透明度
          }

          gl_FragColor = vec4(finalColor, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending, // 关键：叠加混合让它们发光
      depthWrite: false,
    });

    instancedMesh.material = material;
    scene.add(instancedMesh);

    // 交互处理
    const handleMouseMove = e => {
      mouseRef.current.x = e.clientX / window.innerWidth;
      mouseRef.current.y = 1.0 - e.clientY / window.innerHeight;
    };
    window.addEventListener('mousemove', handleMouseMove);

    // 简单的 3D 噪声辅助函数
    const quickNoise = (x, y, z) => {
      return Math.sin(x) * Math.cos(y) * Math.sin(z); // 极简模拟，为了性能
    };

    let frameId;
    let time = 0;

    // 4. 动画循环 (核心物理修改)
    const animate = () => {
      time += 0.015;
      material.uniforms.uTime.value = time;

      // 鼠标平滑稍微快一点
      smoothedMouseRef.current.x +=
        (mouseRef.current.x - smoothedMouseRef.current.x) * 0.15;
      smoothedMouseRef.current.y +=
        (mouseRef.current.y - smoothedMouseRef.current.y) * 0.15;

      // 更新 shader 中的鼠标位置（屏幕坐标，0-1范围）
      material.uniforms.uMouse.value.set(
        smoothedMouseRef.current.x,
        smoothedMouseRef.current.y
      );

      // 计算世界坐标鼠标位置（用于物理碰撞）
      const fov = camera.fov * (Math.PI / 180);
      const height = 2 * Math.tan(fov / 2) * camera.position.z;
      const width = height * camera.aspect;
      const mouseWorld = {
        x: (smoothedMouseRef.current.x - 0.5) * width,
        y: (smoothedMouseRef.current.y - 0.5) * height,
      };

      const dt = 0.016;

      for (let i = 0; i < particleCount; i++) {
        const state = particleStates[i];
        const p = state.position;
        const v = state.velocity;

        // --- 1. 基础流动场 (Curl Noise 模拟) ---
        // 让它们即使不碰也在动
        const noiseScale = 0.2;
        const noiseSpeed = time * 0.5;
        const nx = quickNoise(p.y * noiseScale, p.z * noiseScale, noiseSpeed);
        const ny = quickNoise(
          p.x * noiseScale,
          p.z * noiseScale,
          noiseSpeed + 100
        );
        const nz = quickNoise(
          p.x * noiseScale,
          p.y * noiseScale,
          noiseSpeed + 200
        );

        // 基础漂移力（非常缓慢，只有鼠标接触时才快速移动）
        v.x += nx * 0.02 * dt; // 降低到0.02，让基础流动很慢
        v.y += ny * 0.02 * dt;
        v.z += nz * 0.02 * dt;

        // --- 2. 鼠标交互 (爆炸式斥力) ---
        const dx = p.x - mouseWorld.x;
        const dy = p.y - mouseWorld.y;
        // 只计算 XY 平面的距离，让 Z 轴深处的也能感觉到鼠标
        const distSq = dx * dx + dy * dy;
        const dist = Math.sqrt(distSq);

        const radius = 5.0; // 交互半径

        if (dist < radius) {
          // 计算斥力方向
          const force = (radius - dist) / radius; // 0 到 1

          // 爆炸式斥力：越近推得越狠
          const strength = 50.0; // 提升到50.0以上，爆炸式清空
          const dirX = dx / dist;
          const dirY = dy / dist;

          // 直接修改速度，而不是位置 (物理更自然)
          // force * force 意味着靠近中心时力量是原来的平方倍
          v.x += dirX * force * force * strength * dt;
          v.y += dirY * force * force * strength * dt;

          // 给一点 Z 轴扰动，让它们被撞飞时也会前后移动
          v.z += Math.random() * force * 15.0 * dt;
        }

        // --- 3. 回归力 (弹簧效果) ---
        // 拽回初始位置
        const homeX = state.initialPosition.x - p.x;
        const homeY = state.initialPosition.y - p.y;
        const homeZ = state.initialPosition.z - p.z;

        // 阻尼系数 (Damping)：提升到0.98，让粒子飞得更远，不要马上停下来
        v.x *= 0.98;
        v.y *= 0.98;
        v.z *= 0.98;

        // 拉回原来的位置（降低到0.01，粒子在远处徘徊很久，再极其缓慢地飘回原位）
        const returnStrength = 0.01 * dt;
        v.x += homeX * returnStrength;
        v.y += homeY * returnStrength;
        v.z += homeZ * returnStrength;

        // --- 4. 应用速度 ---
        p.x += v.x;
        p.y += v.y;
        p.z += v.z;

        // 更新矩阵
        tempObject.position.copy(p);

        // 动画：粒子在旋转
        tempObject.rotation.z =
          state.random * Math.PI * 2 + time * (0.2 + state.random);

        // 动画：大小随速度变化 (动得快的时候变细长，像水滴)
        const speed = Math.sqrt(v.x * v.x + v.y * v.y);
        const scaleAnim = 1.0 + speed * 0.5;
        tempObject.scale.set(state.size * scaleAnim, state.size / scaleAnim, 1);

        tempObject.updateMatrix();
        instancedMesh.setMatrixAt(i, tempObject.matrix);
      }

      instancedMesh.instanceMatrix.needsUpdate = true;
      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      material.uniforms.uResolution.value.set(
        window.innerWidth,
        window.innerHeight
      );
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      if (container && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      instancedMesh.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
};

export default FluidGeometryBackground;
